import { useEffect, useRef, useState } from 'react';
import { AgentItem, api, Run, RUN_STATUS, TOOL_LABEL } from '../api';
import { useSession } from '../session';
import { ErrorBox, fmtDate, useAsync } from './ui';

const OPEN = new Set(['queued', 'running']);
const FINAL = new Set(['succeeded', 'failed', 'needs_input', 'cancelled']);

/** Feladat adása ügynöknek egy rekordon + az ehhez a rekordhoz tartozó ügynöki futások. */
export function AgentPanel({ subject, onChange }: { subject: { type: 'company' | 'contact' | 'deal'; id: string }; onChange?: () => void }) {
  const s = useSession();
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [text, setText] = useState('');
  const a = useAsync();
  const timer = useRef<number>();
  // az esemény → ügynök út aszinkron (outbox → laier): a megnyitás utáni fél percben akkor is figyelünk, ha még nincs futás
  const watchUntil = useRef(Date.now() + 30_000);
  const key = `${subject.type}:${subject.id}`;

  const loadRuns = async () => {
    const r = (await api<{ items: Run[] }>(`/agents/runs?subject=${encodeURIComponent(key)}`)).items;
    setRuns((prev) => {
      // ha egy futás most fejeződött be, frissüljön az adatlap és az idővonal is
      // most lezárult futás (korábban nem volt lezárva, vagy még nem láttuk) → adatlap és idővonal frissítése
      const finishedNow = r.filter((x) => FINAL.has(x.status) && !FINAL.has(prev?.find((p) => p.id === x.id)?.status ?? ''));
      if (prev && finishedNow.length) onChange?.();
      return r;
    });
    window.clearTimeout(timer.current);
    if (r.some((x) => OPEN.has(x.status)) || Date.now() < watchUntil.current) timer.current = window.setTimeout(loadRuns, 2500);
  };
  useEffect(() => {
    watchUntil.current = Date.now() + 30_000;
    a.run(async () => {
      setAgents((await api<{ items: AgentItem[] }>('/agents')).items);
      await loadRuns();
    });
    return () => window.clearTimeout(timer.current);
  }, [key]);

  const agent = agents.find((x) => x.delegable && x.enabled);
  const nameOf = (id: string) => agents.find((x) => x.id === id)?.name ?? id;
  const send = async () => {
    if (!agent || !text.trim()) return;
    if (await a.run(() => api('/agents/tasks', { method: 'POST', body: { agent_id: agent.id, instruction: text, subject } }))) {
      setText('');
      watchUntil.current = Date.now() + 30_000;
      await loadRuns();
    }
  };

  return (
    <section className="card">
      <h3>Ügynökök</h3>
      {s.on('ai.delegate') && (agent ? (
        <div className="note-add">
          <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={`Feladat az ügynöknek (${agent.name}), pl. „jegyezd fel: …” vagy „írj ki feladatot holnapra: …”`} />
          <button onClick={send} disabled={a.busy || !text.trim()}>Átadás</button>
        </div>
      ) : <p className="muted">Feladatot az „Ügynökök” oldalon bekapcsolt Asszisztensnek adhatsz.</p>)}
      <ErrorBox error={a.error} />
      <ol className="timeline">
        {runs?.map((r) => (
          <li key={r.id} className="agent">
            <div className="tl-head">
              <strong>{nameOf(r.agent_id)}</strong>
              <span className={`badge ${r.status === 'succeeded' ? 'badge-agent' : r.status === 'waiting_approval' || r.status === 'needs_input' ? 'badge-risk-external' : ''}`}>
                {RUN_STATUS[r.status] ?? r.status}
              </span>
              <span className="muted">{fmtDate(r.created_at)}</span>
            </div>
            {r.plan && <ul className="plan">{r.plan.map((p, i) => <li key={i}>{p}</li>)}</ul>}
            {r.steps.length > 0 && (
              <p className="muted">
                {r.steps.map((st, i) => <span key={i} className="tag">{TOOL_LABEL[st.tool] ?? st.tool}: {st.status === 'ok' ? '✓' : st.status === 'gated' ? 'jóváhagyásra vár' : st.status === 'denied' ? 'elutasítva' : 'hiba'}</span>)}
              </p>
            )}
            {r.result && <p className="pre">{r.result}</p>}
          </li>
        ))}
        {runs?.length === 0 && <li className="muted">Ehhez a rekordhoz még nem dolgozott ügynök.</li>}
      </ol>
    </section>
  );
}
